// /disposable-domains — blacklist de dominios de email para signup (3.29).
//
// Acceso: solo role 'admin'. Lista + agregar + eliminar.
// Estos dominios se rechazan en POST /partners/{id}/dbconnections/signup
// con `400 invalid_request_body`.

import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listDisposableDomains,
  addDisposableDomain,
  deleteDisposableDomain,
  AdminApiError,
} from '@/lib/api';
import { fieldCls } from '@/lib/form-styles';

export default function DisposableDomains(): JSX.Element {
  const qc = useQueryClient();
  const [domain, setDomain] = useState('');
  const [notes, setNotes] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [deletingDomain, setDeletingDomain] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['disposable-domains'],
    queryFn: listDisposableDomains,
  });

  const add = useMutation({
    mutationFn: ({ d, n }: { d: string; n: string }) => addDisposableDomain(d, n || undefined),
    onSuccess: () => {
      setDomain('');
      setNotes('');
      setFormError(null);
      qc.invalidateQueries({ queryKey: ['disposable-domains'] });
    },
    onError: (err) => {
      setFormError(
        err instanceof AdminApiError ? err.message : 'No pudimos agregar el dominio.',
      );
    },
  });

  const remove = useMutation({
    mutationFn: (d: string) => deleteDisposableDomain(d),
    onSettled: () => {
      setDeletingDomain(null);
      qc.invalidateQueries({ queryKey: ['disposable-domains'] });
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setFormError(null);
    const d = domain.trim().toLowerCase();
    if (!d) return;
    add.mutate({ d, n: notes.trim() });
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 data-help-page="disposable.page" data-help="disposable.page" className="text-2xl font-semibold">Blacklist de email</h1>
        <p className="text-sm text-ink-muted max-w-2xl">
          Dominios bloqueados al hacer signup. Útil para frenar mails desechables
          (10minutemail, guerrillamail, etc.) o dominios concretos con abuso. La
          verificación es <code>lowercase(domain)</code> contra el match exacto —
          subdominios no se cubren automáticamente.
        </p>
      </header>

      <section
        data-help="disposable.add"
        className="bg-surface dark:bg-surface-dark rounded-xl border border-surface-border dark:border-surface-dark-border p-5 space-y-3"
      >
        <h2 className="text-sm font-semibold">Agregar dominio</h2>
        <form onSubmit={onSubmit} className="grid gap-3 sm:grid-cols-[1fr,1fr,auto] items-start">
          <label className="text-sm space-y-1">
            <span className="font-medium">Dominio</span>
            <input
              type="text"
              required
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="mailinator.com"
              className={fieldCls({ size: 'lg', mono: true })}
            />
          </label>
          <label className="text-sm space-y-1">
            <span className="font-medium">Notas <span className="text-ink-muted">(opcional)</span></span>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Motivo: spam masivo 2026-05"
              maxLength={1000}
              className={fieldCls({ size: 'lg' })}
            />
          </label>
          <button
            type="submit"
            disabled={add.isPending || !domain.trim()}
            className="min-h-[2.75rem] rounded-lg bg-brand hover:bg-brand-hover text-white font-medium px-4 disabled:opacity-50 self-end"
          >
            {add.isPending ? 'Agregando…' : 'Agregar'}
          </button>
        </form>
        {formError ? (
          <div
            role="alert"
            className="rounded-lg border border-status-error/30 bg-status-error/10 p-3 text-sm text-status-error"
          >
            {formError}
          </div>
        ) : null}
      </section>

      {q.isLoading ? <p className="text-sm text-ink-muted">Cargando…</p> : null}
      {q.isError ? (
        <div
          role="alert"
          className="rounded-lg border border-status-error/30 bg-status-error/10 p-3 text-sm text-status-error"
        >
          {q.error instanceof AdminApiError ? q.error.message : 'No pudimos cargar la lista.'}
        </div>
      ) : null}

      {q.data ? (
        <section className="bg-surface dark:bg-surface-dark rounded-xl border border-surface-border dark:border-surface-dark-border overflow-hidden">
          <div className="px-5 py-3 border-b border-surface-border dark:border-surface-dark-border flex items-center justify-between">
            <p className="text-sm text-ink-muted">
              <strong>{q.data.count}</strong> dominios en la blacklist
            </p>
          </div>
          {q.data.domains.length === 0 ? (
            <p className="p-5 text-sm text-ink-muted">
              Sin dominios bloqueados. Cualquier email se acepta en signup.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase text-ink-muted border-b border-surface-border dark:border-surface-dark-border">
                    <th className="text-left px-5 py-2">Dominio</th>
                    <th className="text-left px-5 py-2">Notas</th>
                    <th className="text-left px-5 py-2">Agregado</th>
                    <th className="text-right px-5 py-2">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.domains.map((d) => (
                    <tr
                      key={d.domain}
                      className="border-b border-surface-border dark:border-surface-dark-border last:border-b-0"
                    >
                      <td className="px-5 py-3 font-mono text-xs">{d.domain}</td>
                      <td className="px-5 py-3 text-xs text-ink-muted max-w-md">
                        {d.notes || <span className="opacity-50">—</span>}
                      </td>
                      <td className="px-5 py-3 text-xs text-ink-muted">
                        {new Date(d.created_at).toLocaleDateString('es-AR', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => {
                            if (
                              window.confirm(
                                `¿Quitar "${d.domain}" de la blacklist?\n\nLos signups con ese dominio se volverán a aceptar.`,
                              )
                            ) {
                              setDeletingDomain(d.domain);
                              remove.mutate(d.domain);
                            }
                          }}
                          disabled={remove.isPending && deletingDomain === d.domain}
                          className="text-sm text-status-error hover:underline disabled:opacity-50"
                        >
                          {remove.isPending && deletingDomain === d.domain
                            ? 'Quitando…'
                            : 'Quitar'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null}
    </div>
  );
}
